<template>
  <div
    class="text-medium action-links"
    :class="{ 'has-bg': !hideSaveAsList }"
    v-if="hasFilterChanged || showSaveAsList"
  >
    <b-button class="py-1" variant="primary" v-if="selectedListName" @click="saveChanges" pill>
      Save
    </b-button>
    <b-button
      class="py-1"
      variant="primary"
      v-if="selectedListName && hasFilterChanged"
      @click="onSaveClick"
      pill
    >
      Save as...
    </b-button>
    <b-button class="py-1" variant="primary" @click="onSaveClick" v-else-if="!hideSaveAsList" pill>
      Save as view
    </b-button>
    <link-button link-color="primary" @click="$emit('reset')" class="text-medium p-0 mr-2">
      Reset
    </link-button>
    <modal
      :show="showModal"
      size="md"
      @hidden="hideModal"
      submit-button-text="Save this view"
      cancel-button-text="I change my mind"
      title="Save this view"
      @submit="onSubmit"
    >
      <p class="text-secondary">
        Create views based on filters you often use, for example all
        {{ ENTITY_MAP[entityType].namePlural }} that are at risk, or
        {{ ENTITY_MAP[entityType].namePlural }} that are not updated.
      </p>
      <b-form @submit.stop.prevent="onSubmit" class="pb-3">
        <label for="list-name">Name your view</label>
        <b-form-input
          autocomplete="off"
          v-model="name"
          id="list-name"
          placeholder="View name"
          autofocus
        ></b-form-input>
        <b-form-invalid-feedback :state="!errorMessage">
          {{ errorMessage }}
        </b-form-invalid-feedback>
      </b-form>
    </modal>
  </div>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton'
import Modal from '@/components/Modal'
import { ENTITY_MAP } from '@/constants/entities'
import events from '@/constants/events'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBusMixin from '@/mixins/EventBusMixin'
import UserListsMixin from '@/mixins/UserListsMixin'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import { BButton, BForm, BFormInput, BFormInvalidFeedback } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This provides buttons/links to create/save/reset user views
 */
export default {
  name: 'SaveUserList',
  props: [
    'filterData',
    'listName',
    'listId',
    'subOrgId',
    'orgId',
    'teamId',
    'hideSaveAsList',
    'entityType',
    'hasFilterChanged',
    'showSaveAsList' // hideSaveAsList & showSaveAsList take highest priority. If either of them are set, visibility of save buttons does not depend on filterData
  ],
  mixins: [EventBusMixin, UserListsMixin],
  components: {
    Modal,
    BForm,
    BFormInput,
    BFormInvalidFeedback,
    LinkButton,
    BButton
  },
  data() {
    return {
      showModal: false,
      name: null,
      errorMessage: null,
      ENTITY_MAP
    }
  },
  computed: {
    userId() {
      return this.$store.state.user.id
    }
  },
  methods: {
    ...mapActions('userLists', ['createUserList', 'updateUserList']),
    onSaveClick() {
      this.showModal = true
    },
    async onSubmit() {
      if (this.name && !this.errorMessage) {
        try {
          const response = await this.postUserList()
          showToast('View saved successfully')
          this.emitChangeEvent()
          this.navigateToNewList(response)
        } catch (e) {
          console.warn('Caught error while creating user view', e)
          if (e.response.data.code === 'view_already_present') {
            showToast('Similar view is already available', TOAST_TYPE.ERROR)
          } else {
            showAPIError(e, undefined, 'Error while saving user views')
          }
        }
        this.hideModal()
      }
    },
    getFilterDataToSave() {
      const filterData = R.omit(['listId'], this.filterData)
      if (this.teamId && !filterData.teamIds) {
        // when list is created from team page, inject teamId into filterData
        filterData.teamIds = this.teamId.toString()
      }
      return filterData
    },
    async postUserList() {
      const payload = {
        organizationId: this.orgId,
        name: this.name,
        filterData: this.getFilterDataToSave(),
        teamId: this.subOrgId,
        entityType: this.entityType
      }
      //add team id
      return this.createUserList(payload)
    },
    navigateToNewList(list) {
      const location = this.getListLocation(list)
      this.$router.push(location)
    },
    emitChangeEvent() {
      this.eventBusEmit(events.USER_LIST_CHANGED)
    },
    async saveChanges() {
      try {
        const payload = {
          organizationId: this.orgId,
          filterData: this.getFilterDataToSave(),
          id: this.selectedListId,
          teamId: this.subOrgId,
          name: this.name
        }
        await this.updateUserList(payload)
        this.emitChangeEvent()
        this.$emit('save-changes')
        showToast('Changes saved successfully')
      } catch (e) {
        console.error(e)
        showToast('Error while updating user view', TOAST_TYPE.ERROR)
      }
    },
    hideModal() {
      this.showModal = false
      this.name = ''
    }
  },
  mounted() {},
  watch: {
    name(newTitle) {
      if (newTitle.length === 0 || newTitle.length >= 30)
        this.errorMessage = 'View name should be between 1-30 chars.'
      else {
        this.errorMessage = ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/forms';
@import '~@/styles/_styleConfigurations.scss';

/deep/ .is-valid {
  border-color: inherit;
}

.action-links {
  display: flex;
  gap: 12px;
  padding: 6px;
  border-radius: 50px;

  &.has-bg {
    background-color: $brandQuinary;
  }
}
</style>
